import { ref, reactive } from 'vue'
import { useForm, useField } from 'vee-validate'
import { object, number, string, array, date, boolean } from 'yup'
import { usePoliticalActions } from './usePoliticalActions'
import dayjs from 'dayjs'
import { useImgUploader } from '../useImgUploader'
import { expenseTypesToArray } from '../useExpenseType'

const tabs = reactive([
  {
    name: '基本情報',
    required: true,
    status: 0,
    fields: [''],
  },
  {
    name: '内容',
    required: true,
    status: 0,
    fields: [],
  },
  {
    name: '領収書添付',
    required: true,
    status: 0,
    fields: [],
  },
])

const schema = object({
  type: array()
    .required('必須です')
    .typeError('必須です'),
  from: string()
    .required('必須です')
    .typeError('必須です'),
  to: string()
    .required('必須です')
    .typeError('必須です'),
  people: number('整数を').when(['type'], {
    is: type => type && type.length && type[0].id == 1,
    then: number('整数を入力')
      .required('必須です')
      .min(1)
      .integer('整数で入力してください'),
  }),
  detail: string().typeError('詳細を入力してください'),
  stayDays: number('整数で入力してください')
    .integer('整数で入力してください')
    .typeError('整数で入力してください'),
  amount: number('整数で入力してください')
    .integer('整数で入力してください')
    .typeError('整数で入力してください'),
  item: string(),
  isRoundTrip: boolean(),
  distance: number('数値を入力してください').positive(),
  expenseTypeId: array(),
  expenseAmount: number('整数で入力してください')
    .required('必須です')
    .integer('整数で入力してください')
    .typeError('整数で入力してください')
    .positive('正の整数で入力してください'),
  expenseDate: date('必須です')
    .required('必須です')
    .typeError('必須です'),
  expenseContent1: string()
    .required()
    .typeError('必須です'),
  expenseContent2: string()
    .required()
    .typeError('必須です'),
  isDistributed: boolean(),
  numerator: number('数値を入力してください'),
  denominator: number('数値を入力してください'),
})

// type
const { value: type, errorMessage: typeError } = useField('type', undefined, {
  initialValue: [],
})
// from
const { value: from, errorMessage: fromError } = useField('from', undefined)
// to
const { value: to, errorMessage: toError } = useField('to', undefined)
// people
const { value: people, errorMessage: peopleError } = useField(
  'people',
  undefined,
  0,
)
// detail
const { value: detail, errorMessage: detailError } = useField('detail')
// stayDays
const { value: stayDays, errorMessage: stayDaysError } = useField('stayDays')
// amount
const { value: amount, errorMessage: amountError } = useField('amount')
// isRoundTrip
const { value: isRoundTrip, errorMessage: isRoundTripError } = useField(
  'isRoundTrip',
  undefined,
  {
    initialValue: false,
  },
)
// distance
const { value: distance, errorMessage: distanceError } = useField('distance')
// item
const { value: item, errorMessage: itemError } = useField('item')
// receipt/expenseTypeId
const {
  value: expenseTypeId,
  errorMessage: expenseTypeIdError,
} = useField('expenseTypeId', undefined, { initialValue: [] })
// receipt/expenseAmount
const { value: expenseAmount, errorMessage: expenseAmountError } = useField(
  'expenseAmount',
)
// receipt/expenseDate
const {
  value: expenseDate,
  errorMessage: expenseDateError,
} = useField('expenseDate', undefined, { initialValue: undefined })
// receipt/expenseContent1
const { value: expenseContent1, errorMessage: expenseContent1Error } = useField(
  'expenseContent1',
)
// receipt/expenseContent2
const { value: expenseContent2, errorMessage: expenseContent2Error } = useField(
  'expenseContent2',
)
// receipt/isDistributed
const {
  value: isDistributed,
  errorMessage: isDistributedError,
} = useField('isDistributed', undefined, { initialValue: false })
// receipt/numerator
const { value: numerator, errorMessage: numeratorError } = useField(
  'numerator',
  undefined,
  { initialValue: undefined },
)
// receipt/denominator
const {
  value: denominator,
  errorMessage: denominatorError,
} = useField('denominator', undefined, { initialValue: undefined })
// receipt/images
const { value: images, errorMessage: imagesError } = useField(
  'images',
  undefined,
  {
    initialValue: [],
  },
)

// TODO: spliceで配列を操作しているが検知できないため（原因特定できず）、暫定的に再描画するためのフラグを設定した。リファクタリング候補
const isShowingMemoryItem = ref(true)

/**
 * @var expense配列のユニークキー
 */
const expenseId = ref(null)

export const useMemoryItemFormTabs = () => {
  return { tabs }
}

export const useMemoryItemForm = () => {
  const { meta, handleSubmit } = useForm({ validationSchema: schema })

  const onSubmit = handleSubmit(values => {
    alert(JSON.stringify(values, null, 2))
  }, onInvalidSubmit)

  const onInvalidSubmit = ({ results, errors }) => {
    console.log(results)
    console.log(errors)
  }

  const getFromLabel = typeId => {
    switch (typeId) {
      case 1:
        return '出発駅'
      case 2:
        return '出発地'
      case 3:
        return '出発地'
      case 5:
        return '出発地'
      default:
        console.debug('invalid type id: ' + typeId)
    }
    return ''
  }

  const getToLabel = typeId => {
    switch (typeId) {
      case 1:
        return '到着駅'
      case 2:
        return '到着地'
      case 3:
        return '到着地'
      case 5:
        return '到着地'
      default:
        console.debug('invalid type id: ' + typeId)
    }
    return ''
  }

  const initMemoryItemForm = () => {
    // 初期化
    expenseId.value = null
    type.value = []
    from.value = undefined
    to.value = undefined
    people.value = undefined
    detail.value = undefined
    stayDays.value = undefined
    amount.value = undefined
    isRoundTrip.value = false
    distance.value = undefined
    expenseTypeId.value = []
    item.value = undefined
    expenseAmount.value = undefined
    expenseDate.value = undefined
    expenseContent1.value = undefined
    expenseContent2.value = undefined
    isDistributed.value = false
    numerator.value = undefined
    denominator.value = undefined
    images.value = []
  }

  const setMemoryItemForm = memoryItem => {
    // 初期化
    initMemoryItemForm()

    // パラメータセット

    expenseId.value = memoryItem?.id ?? null
    type.value = memoryItem?.type ? memoryItem?.type : []
    from.value = memoryItem?.from
    to.value = memoryItem?.to
    isRoundTrip.value = memoryItem?.isRoundTrip ?? false
    people.value = memoryItem?.people
    distance.value = memoryItem?.distance
    amount.value = memoryItem?.amount
    detail.value = memoryItem?.detail
    item.value = memoryItem?.item
    // TODO: transportation未記入、追加する
    expenseTypeId.value = memoryItem?.receipt?.expenseTypeId
      ? expenseTypesToArray(memoryItem?.receipt?.expenseTypeId)
      : []
    expenseAmount.value = memoryItem?.receipt?.expenseAmount
    isDistributed.value = memoryItem?.receipt?.isDistributed ?? false
    numerator.value = memoryItem?.receipt?.numerator
    denominator.value = memoryItem?.receipt?.denominator
    expenseDate.value = memoryItem?.receipt?.expenseDate
      ? new Date(memoryItem.receipt.expenseDate)
      : undefined
    expenseContent1.value = memoryItem?.receipt?.expenseContent1
    expenseContent2.value = memoryItem?.receipt?.expenseContent2
    images.value = memoryItem?.receipt?.images ?? []
    const { imgUploader } = useImgUploader()
    imgUploader.imgs = images.value
  }

  const renderComponent = ref(true)

  const mergeExpenses = () => {
    isShowingMemoryItem.value = false
    const isAddition = expenseId.value === null
    if (isAddition) {
      addExpenses()
    } else {
      updateExpenses()
    }
    isShowingMemoryItem.value = true
  }

  /**
   * 記録簿を追加する\
   * FIXME: リファクタリング候補\
   * オブジェクト指向で設計するならばcurrentPoliticalActionを持つクラスが担った方が良い
   */
  const addExpenses = () => {
    const { currentPoliticalAction } = usePoliticalActions()
    const maxId =
      currentPoliticalAction.memory.expenses
        ?.map(expense => expense.id)
        ?.reduce((lId, rId) => Math.max(lId, rId), -2) ?? -1
    const expense = {
      id: maxId + 1,
      type: type.value,
      from: from.value,
      to: to.value,
      isRoundTrip: isRoundTrip.value,
      people: people.value,
      distance: distance.value,
      stayDays: stayDays.value,
      amount: amount.value,
      detail: detail.value,
      item: item.value,
      // transportation: type.value, // TODO: 宿泊パックの場圧のパラメーター、追加する
      receipt: {
        expenseTypeId: expenseTypeId.value[0]?.id ?? null,
        expenseAmount: expenseAmount.value,
        isDistributed: isDistributed.value,
        numerator: numerator.value,
        denominator: denominator.value,
        expenseDate: dayjs(expenseDate.value).format('YYYY-MM-DD HH:mm:ss'),
        expenseContent1: expenseContent1.value,
        expenseContent2: expenseContent2.value,
        images: images.value,
      },
    }
    currentPoliticalAction.memory.expenses.push(expense)
  }

  const updateExpenses = () => {
    const { currentPoliticalAction } = usePoliticalActions()
    currentPoliticalAction.memory.expenses = currentPoliticalAction.memory.expenses.map(
      expense => {
        if (expense.id === expenseId.value) {
          return {
            id: expenseId.value,
            type: type.value,
            from: from.value,
            to: to.value,
            isRoundTrip: isRoundTrip.value,
            people: people.value,
            distance: distance.value,
            stayDays: stayDays.value,
            amount: amount.value,
            detail: detail.value,
            item: item.value,
            // transportation: type.value, // TODO: 宿泊パックの場圧のパラメーター、追加する
            receipt: {
              expenseTypeId: expenseTypeId.value[0]?.id ?? null,
              expenseAmount: expenseAmount.value,
              isDistributed: isDistributed.value,
              numerator: numerator.value,
              denominator: denominator.value,
              expenseDate: dayjs(expenseDate.value).format(
                'YYYY-MM-DD HH:mm:ss',
              ),
              expenseContent1: expenseContent1.value,
              expenseContent2: expenseContent2.value,
              images: images.value,
            },
          }
        } else {
          return expense
        }
      },
    )
  }

  /**
   * 記録簿から削除する
   * FIXME: リファクタリング候補\
   * オブジェクト指向で設計するならばcurrentPoliticalActionを持つクラスが担った方が良い
   *
   * @param {object} expense politicalAction.memory.expenses配列内のオブジェクト
   * @returns void
   */
  const removeExpenseFromCurrentPoliticalAction = expense => {
    const { currentPoliticalAction } = usePoliticalActions()
    const expenses = currentPoliticalAction.memory.expenses
    const index = expenses.findIndex(currentExpense => {
      return currentExpense.id === expense.id
    })

    isShowingMemoryItem.value = false
    currentPoliticalAction.memory.expenses.splice(index, 1)
    isShowingMemoryItem.value = true
  }

  const updateImages = () => {
    const { imgUploader } = useImgUploader()
    images.value = imgUploader.imgs
  }

  return {
    expenseId,
    renderComponent,
    type,
    typeError,
    people,
    peopleError,
    from,
    fromError,
    to,
    toError,
    detail,
    detailError,
    stayDays,
    amount,
    amountError,
    stayDaysError,
    item,
    itemError,
    isRoundTrip,
    isRoundTripError,
    distance,
    distanceError,
    expenseTypeId,
    expenseTypeIdError,
    expenseAmount,
    expenseAmountError,
    expenseDate,
    expenseDateError,
    expenseContent1,
    expenseContent1Error,
    expenseContent2,
    expenseContent2Error,
    isDistributed,
    isDistributedError,
    numerator,
    numeratorError,
    denominator,
    denominatorError,
    images,
    imagesError,
    meta,
    isShowingMemoryItem,
    onSubmit,
    onInvalidSubmit,
    getFromLabel,
    getToLabel,
    mergeExpenses,
    removeExpenseFromCurrentPoliticalAction,
    initMemoryItemForm,
    setMemoryItemForm,
    updateImages,
    expenseTypesToArray,
  }
}
